/**
 * Sticky Add-to-Cart Component
 * 
 * Displays a sticky add-to-cart bar at the bottom of the viewport when the main
 * product form is out of view. Syncs variant changes from the main picker and
 * handles trigger clicks to either open the ring size layer or scroll to picker.
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

class StickyAddToCartComponent extends Component {
  #observer = null;
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    this.#relocateToMain();
    this.#setupIntersectionObserver();
    this.#setupEventListeners(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroyObserver();

    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
  }

  /**
   * Move the element to be a direct child of <main> so that
   * position: sticky; bottom: 0 stops it before the footer
   */
  #relocateToMain() {
    const main = document.getElementById('MainContent');
    if (!main || this.parentElement === main) return;

    main.appendChild(this);
  }

  /**
   * Setup IntersectionObserver to monitor the main product form visibility
   */
  #setupIntersectionObserver() {
    const productFormId = this.dataset.productFormId;
    if (!productFormId) return;

    const productForm = document.getElementById(productFormId);
    if (!productForm) return;

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    };

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.classList.toggle('is-visible', !entry.isIntersecting);
      }
    }, options);

    this.#observer.observe(productForm);
  }

  /**
   * Destroy the IntersectionObserver
   */
  #destroyObserver() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }

  /**
   * Setup event listeners for variant updates
   */
  #setupEventListeners(signal) {
    document.addEventListener(
      ThemeEvents.variantUpdate,
      (event) => this.#onVariantUpdate(event),
      { signal }
    );
  }

  /**
   * Handle variant update events from the main picker
   */
  #onVariantUpdate(event) {
    const { resource: variant, data } = event.detail;
    if (!data?.html) return;
    if (data.productId !== this.dataset.productId) return;

    const newDocument = data.html;
    const newStickyElement = newDocument.getElementById(this.id);
    if (!newStickyElement) return;

    // Update image
    const currentImage = this.refs.image;
    const newImage = newStickyElement.querySelector('[ref="image"]');
    if (currentImage && newImage) {
      morph(currentImage, newImage);
    }

    // Update title
    const currentTitle = this.refs.title;
    const newTitle = newStickyElement.querySelector('[ref="title"]');
    if (currentTitle && newTitle) {
      morph(currentTitle, newTitle);
    }

    // Update options list (includes trigger button if Ring Size)
    const currentOptions = this.querySelector('[ref="options"]');
    const newOptions = newStickyElement.querySelector('[ref="options"]');
    if (currentOptions && newOptions) {
      morph(currentOptions, newOptions);
    }

    // Update button text (inside add-to-cart-component, use querySelector)
    const currentButtonText = this.querySelector('[ref="buttonText"]');
    const newButtonText = newStickyElement.querySelector('[ref="buttonText"]');
    if (currentButtonText && newButtonText) {
      morph(currentButtonText, newButtonText);
    }

    // Update button price (inside add-to-cart-component, use querySelector)
    const currentButtonPrice = this.querySelector('[ref="buttonPrice"]');
    const newButtonPrice = newStickyElement.querySelector('[ref="buttonPrice"]');
    if (currentButtonPrice && newButtonPrice) {
      morph(currentButtonPrice, newButtonPrice);
    }

    // Update button disabled state (inside add-to-cart-component, use querySelector)
    const currentButton = this.querySelector('[ref="addToCartButton"]');
    const newButton = newStickyElement.querySelector('[ref="addToCartButton"]');
    if (currentButton && newButton) {
      if (newButton.hasAttribute('disabled')) {
        currentButton.setAttribute('disabled', '');
      } else {
        currentButton.removeAttribute('disabled');
      }
    }

    // Update data-product-variant-media on add-to-cart-component
    const currentAddToCartContainer = this.refs.addToCartContainer;
    const newAddToCartContainer = newStickyElement.querySelector('[ref="addToCartContainer"]');
    if (currentAddToCartContainer && newAddToCartContainer) {
      const newMedia = newAddToCartContainer.dataset.productVariantMedia;
      if (newMedia) {
        currentAddToCartContainer.dataset.productVariantMedia = newMedia;
      }
    }
  }

  /**
   * Handle trigger button click
   * Opens ring size layer or scrolls to variant picker based on option type
   */
  handleTriggerClick = () => {
    const optionType = this.dataset.optionType;
    const sectionId = this.dataset.sectionId;

    if (optionType && optionType.includes('Size')) {
      const layerDialogId = `ring-size-layer-${sectionId}-dialog`;
      const layerDialog = document.getElementById(layerDialogId);

      if (layerDialog && layerDialog instanceof HTMLDialogElement) {
        layerDialog.showModal();
      }
    } else {
      const variantPickerId = `VariantPicker-${sectionId}`;
      const variantPicker = document.getElementById(variantPickerId);

      if (variantPicker) {
        variantPicker.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  };
}

customElements.define('sticky-add-to-cart-component', StickyAddToCartComponent);
